import { AlertCircle, Clock, Zap } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { getWorkEstimate } from '../../api/workApi'
import type { WorkEstimate, WorkSubcategory } from '../../types/work'

interface WorkPriceEstimateProps {
  subcategory: WorkSubcategory
  inputText?: string
  durationSeconds?: number
  imageCount?: number
}

export default function WorkPriceEstimate({
  subcategory,
  inputText = '',
  durationSeconds,
  imageCount = 1,
}: WorkPriceEstimateProps) {
  const [estimate, setEstimate] = useState<WorkEstimate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) {clearTimeout(debounceRef.current)}

    debounceRef.current = setTimeout(() => {
      void (async () => {
        setLoading(true)
        setError(null)
        try {
          const result = await getWorkEstimate({
            subcategory,
            input_length: inputText.length,
            duration_seconds: durationSeconds,
            image_count: imageCount,
          })
          setEstimate(result)
        } catch {
          setError('Could not calculate estimate')
        } finally {
          setLoading(false)
        }
      })()
    }, 500)

    return () => {
      if (debounceRef.current) {clearTimeout(debounceRef.current)}
    }
  }, [subcategory, inputText, durationSeconds, imageCount])

  if (loading) {
    return (
      <div
        style={{
          padding: '12px 16px',
          borderRadius: '10px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <div
          style={{
            width: '60px',
            height: '20px',
            borderRadius: '4px',
            background: 'rgba(255,255,255,0.08)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          padding: '10px 14px',
          borderRadius: '10px',
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          color: 'var(--danger, #ef4444)',
        }}
      >
        <AlertCircle size={14} />
        {error}
      </div>
    )
  }

  if (!estimate) {return null}

  const { flz_cost, breakdown, estimated_time_seconds } = estimate

  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: '10px',
        background: 'rgba(99, 102, 241, 0.08)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
      }}
    >
      {/* Cost */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Zap size={14} style={{ color: 'var(--primary, #6366f1)' }} />
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Estimated Cost</span>
        </div>
        <span
          style={{
            fontSize: '18px',
            fontWeight: 700,
            color: 'var(--primary, #6366f1)',
          }}
        >
          {flz_cost.toFixed(2)} FLZ
        </span>
      </div>

      {/* Breakdown */}
      <div
        style={{
          fontSize: '11px',
          color: 'var(--text-muted, #6b7280)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>
          {breakdown.base_cost} FLZ × {breakdown.quantity} {breakdown.unit.replace(/_/g, ' ')}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Clock size={11} />
          <span>~{estimated_time_seconds}s</span>
        </div>
      </div>
    </div>
  )
}
